import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router';

import ErrorBoundary from '@core/components/ErrorBoundary';
import { Helmet } from 'react-helmet-async';
import { RouteResource } from '@root/core/constants/resource';
import TopBarProgress from 'react-topbar-progress-indicator';
import { store } from '@root/App';

TopBarProgress.config({
  barColors: {
    0: '#FF5F6D',
    '1.0': '#FF5F6D'
  }
});

const LoadableLoading = ({ delay = 300 }) => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => setShow(true), delay);
    return () => {
      clearTimeout(timeout);
    };
  }, [delay]);

  return show ? <TopBarProgress /> : null;
};

const RouteWithTitle = ({
  exact,
  title,
  path,
  module,
  page,
  reducer
}: RouteResource) => {
  const lazyComponent = lazy(() =>
    import(
      /* webpackChunkName: "[request]" */
      `@modules/${module}/pages/${page}`
    ).then(async (component) => {
      if (reducer) {
        if (Array.isArray(reducer)) {
          const reducers = await Promise.all(
            reducer.map(async (r) => {
              const m = await import(
                /* webpackChunkName: "[request]" */
                `@modules/${module}/reducers/${r.resource}`
              );

              return { name: r.name, default: m.default };
            })
          );

          reducers.forEach((reducer) =>
            store.addReducer(reducer.name, reducer.default)
          );
        } else {
          const m = await import(
            /* webpackChunkName: "[request]" */
            `@modules/${module}/reducers/${reducer.resource}`
          );

          store.addReducer(reducer.name, m.default);
        }
      }

      return component;
    })
  );

  return (
    <ErrorBoundary>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <Route exact={exact} path={path} component={lazyComponent} />
    </ErrorBoundary>
  );
};

interface Props {
  routes: RouteResource[];
  authenticated: boolean;
  appRole?: string;
}

const Routes = ({ routes, authenticated, appRole }: Props) => {
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === '/') {
      if (authenticated) {
        history.push('/');
      }
    }
  }, [authenticated, history, location]);

  return (
    <Suspense fallback={<LoadableLoading />}>
      <Switch>
        {routes.map((route) => (
          <RouteWithTitle key={route.path} {...route} />
        ))}
        {authenticated ? (
          <RouteWithTitle
            path="*"
            title="Not Found"
            module="System"
            page="NotFound"
          />
        ) : (
          <Route
            path="*"
            render={(props) => (
              <Redirect
                {...props}
                to={
                  appRole && appRole == 'seller-admin'
                    ? '/admin/login'
                    : appRole == 'warehouse-admin'
                    ? '/admin/login-warehouse'
                    : appRole == 'forwarder-admin'
                    ? '/admin/login-forwarder'
                    : appRole == 'seller-api'
                    ? '/admin/login-seller-api'
                    : appRole == 'warehouse-api'
                    ? '/admin/login-warehouse-api'
                    : '/admin/login-busk'
                }
              />
            )}
          />
        )}
      </Switch>
    </Suspense>
  );
};

Routes.defaultProps = {
  routes: []
};

export default Routes;
